import React, { useEffect, useState, useMemo } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CalendarDays, Plus, List, CalendarRange, Send, Archive, Pencil } from "lucide-react";
import { toast } from "sonner";

const TYPE_COLOR = {
  "Vacances scolaires": "bg-sky-100 text-sky-700",
  "Jour férié": "bg-emerald-100 text-emerald-700",
  "Fermeture exceptionnelle": "bg-red-100 text-red-700",
  "Rentrée scolaire": "bg-indigo-100 text-indigo-700",
  "Fin d'année": "bg-amber-100 text-amber-700",
  "Examen": "bg-purple-100 text-purple-700",
  "Réunion parents": "bg-teal-100 text-teal-700",
  "Événement école": "bg-orange-100 text-orange-700",
  "Autre": "bg-slate-100 text-slate-600",
};
const STATUS_COLOR = { "Brouillon": "bg-slate-100 text-slate-500", "Publié": "bg-emerald-100 text-emerald-700", "Archivé": "bg-slate-200 text-slate-400" };
const MONTHS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];
const EMPTY = { title: "", type: "Vacances scolaires", start_date: "", end_date: "", description_public: "", notes_internal: "", affects_timetable: false };

export default function SchoolCalendar() {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const canManage = hasRole("super_admin", "direction", "administration");
  const [events, setEvents] = useState([]);
  const [types, setTypes] = useState([]);
  const [fType, setFType] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [dlg, setDlg] = useState({ open: false, form: EMPTY, id: null });

  const load = () => {
    const params = {};
    if (fType !== "all") params.type = fType;
    if (canManage && fStatus !== "all") params.status = fStatus;
    api.get("/calendar/events", { params }).then((r) => setEvents(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
  };
  useEffect(() => { load(); }, [fType, fStatus]); // eslint-disable-line
  useEffect(() => { api.get("/calendar/types").then((r) => setTypes(r.data.types || [])).catch(() => {}); }, []);

  const save = async () => {
    const f = dlg.form;
    if (!f.title.trim() || !f.start_date) { toast.error(t("fillRequired")); return; }
    try {
      if (dlg.id) await api.put(`/calendar/events/${dlg.id}`, f);
      else await api.post("/calendar/events", { ...f, is_test: false });
      toast.success(t("save")); setDlg({ open: false, form: EMPTY, id: null }); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const act = async (id, path) => { try { await api.patch(`/calendar/events/${id}/${path}`); toast.success(t("save")); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const edit = (ev) => setDlg({ open: true, id: ev.id, form: { title: ev.title, type: ev.type, start_date: ev.start_date, end_date: ev.end_date, description_public: ev.description_public || "", notes_internal: ev.notes_internal || "", affects_timetable: !!ev.affects_timetable } });

  const set = (k, v) => setDlg((d) => ({ ...d, form: { ...d.form, [k]: v } }));

  return (
    <div data-testid="school-calendar">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <CalendarDays className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-display font-bold text-slate-900">{t("schoolCalendar")}</h1>
        </div>
        {canManage && <Button className="gap-2" data-testid="add-event-btn" onClick={() => setDlg({ open: true, form: EMPTY, id: null })}><Plus className="h-4 w-4" />{t("addEvent")}</Button>}
      </div>

      <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="calendar-filters">
        <Select value={fType} onValueChange={setFType}>
          <SelectTrigger className="w-52" data-testid="filter-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("allTypes")}</SelectItem>
            {types.map((ty) => <SelectItem key={ty} value={ty}>{ty}</SelectItem>)}
          </SelectContent>
        </Select>
        {canManage && (
          <Select value={fStatus} onValueChange={setFStatus}>
            <SelectTrigger className="w-44" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allStatuses")}</SelectItem>
              <SelectItem value="Brouillon">Brouillon</SelectItem>
              <SelectItem value="Publié">Publié</SelectItem>
              <SelectItem value="Archivé">Archivé</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      <Tabs defaultValue="list">
        <TabsList>
          <TabsTrigger value="list" data-testid="tab-list"><List className="h-4 w-4 mr-2" />{t("listView")}</TabsTrigger>
          <TabsTrigger value="month" data-testid="tab-month"><CalendarRange className="h-4 w-4 mr-2" />{t("monthView")}</TabsTrigger>
        </TabsList>

        <TabsContent value="list">
          <div className="space-y-2" data-testid="events-list">
            {events.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">{t("noData")}</p> :
              events.map((ev) => (
                <Card key={ev.id} className="border-slate-200 p-3 flex items-start justify-between gap-3 flex-wrap" data-testid="event-item">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-800">{ev.title}</span>
                      <Badge className={`border-0 text-xs ${TYPE_COLOR[ev.type] || "bg-slate-100"}`}>{ev.type}</Badge>
                      {canManage && <Badge className={`border-0 text-xs ${STATUS_COLOR[ev.status]}`} data-testid={`event-status-${ev.id}`}>{ev.status}</Badge>}
                      {ev.affects_timetable && <Badge className="bg-amber-50 text-amber-600 border-0 text-xs">{t("affectsTimetable")}</Badge>}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{ev.start_date}{ev.end_date && ev.end_date !== ev.start_date ? ` → ${ev.end_date}` : ""}</div>
                    {ev.description_public && <div className="text-xs text-slate-600 mt-1">{ev.description_public}</div>}
                    {canManage && ev.notes_internal && <div className="text-[11px] text-slate-400 mt-0.5 italic">🔒 {ev.notes_internal}</div>}
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="sm" variant="ghost" className="gap-1 h-8" data-testid={`edit-event-${ev.id}`} onClick={() => edit(ev)}><Pencil className="h-4 w-4" />{t("edit")}</Button>
                      {ev.status !== "Publié" && <Button size="sm" variant="outline" className="gap-1 h-8 text-emerald-700 border-emerald-200" data-testid={`publish-event-${ev.id}`} onClick={() => act(ev.id, "publish")}><Send className="h-4 w-4" />{t("publish")}</Button>}
                      {ev.status !== "Archivé" && <Button size="sm" variant="ghost" className="gap-1 h-8 text-slate-500" data-testid={`archive-event-${ev.id}`} onClick={() => act(ev.id, "archive")}><Archive className="h-4 w-4" />{t("archive")}</Button>}
                    </div>
                  )}
                </Card>
              ))}
          </div>
        </TabsContent>

        <TabsContent value="month">
          <MonthGrid events={events} />
        </TabsContent>
      </Tabs>

      <Dialog open={dlg.open} onOpenChange={(o) => setDlg((d) => ({ ...d, open: o }))}>
        <DialogContent data-testid="event-dialog">
          <DialogHeader>
            <DialogTitle>{dlg.id ? t("edit") : t("addEvent")}</DialogTitle>
            <DialogDescription>{t("schoolCalendar")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs text-slate-500">{t("title")}</label><Input data-testid="event-title" value={dlg.form.title} onChange={(e) => set("title", e.target.value)} /></div>
            <div><label className="text-xs text-slate-500">{t("dossierType")}</label>
              <Select value={dlg.form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="event-type"><SelectValue /></SelectTrigger>
                <SelectContent>{types.map((ty) => <SelectItem key={ty} value={ty}>{ty}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-500">{t("startDate")}</label><Input type="date" data-testid="event-start" value={dlg.form.start_date} onChange={(e) => set("start_date", e.target.value)} /></div>
              <div><label className="text-xs text-slate-500">{t("endDate")}</label><Input type="date" data-testid="event-end" value={dlg.form.end_date} onChange={(e) => set("end_date", e.target.value)} /></div>
            </div>
            <div><label className="text-xs text-slate-500">{t("publicDescription")}</label><Textarea data-testid="event-desc" value={dlg.form.description_public} onChange={(e) => set("description_public", e.target.value)} /></div>
            <div><label className="text-xs text-slate-500">{t("internalNotes")}</label><Textarea data-testid="event-notes" value={dlg.form.notes_internal} onChange={(e) => set("notes_internal", e.target.value)} /></div>
            <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" data-testid="event-affects" checked={dlg.form.affects_timetable} onChange={(e) => set("affects_timetable", e.target.checked)} />{t("affectsTimetable")}</label>
          </div>
          <DialogFooter><Button data-testid="event-save-btn" onClick={save}>{t("save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MonthGrid({ events }) {
  const [cursor, setCursor] = useState(() => new Date());
  const { grid, label } = useMemo(() => {
    const y = cursor.getFullYear(), m = cursor.getMonth();
    const first = new Date(y, m, 1);
    const startDow = (first.getDay() + 6) % 7; // Monday-first
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const iso = `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const evs = events.filter((e) => e.start_date <= iso && (e.end_date || e.start_date) >= iso);
      cells.push({ d, iso, evs });
    }
    return { grid: cells, label: `${MONTHS[m]} ${y}` };
  }, [cursor, events]);

  return (
    <Card className="border-slate-200 p-4" data-testid="month-grid">
      <div className="flex items-center justify-between mb-3">
        <Button size="sm" variant="ghost" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>‹</Button>
        <span className="text-sm font-semibold text-slate-700">{label}</span>
        <Button size="sm" variant="ghost" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>›</Button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-slate-400 mb-1">
        {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {grid.map((c, i) => (
          <div key={i} className={`min-h-[64px] rounded-lg border p-1 text-left ${c ? "border-slate-100" : "border-transparent"}`}>
            {c && <>
              <div className="text-[11px] text-slate-400">{c.d}</div>
              <div className="space-y-0.5">
                {c.evs.slice(0, 2).map((e) => <div key={e.id} className={`truncate rounded px-1 text-[10px] ${TYPE_COLOR[e.type] || "bg-slate-100"}`} title={e.title}>{e.title}</div>)}
                {c.evs.length > 2 && <div className="text-[10px] text-slate-400">+{c.evs.length - 2}</div>}
              </div>
            </>}
          </div>
        ))}
      </div>
    </Card>
  );
}
